import { Injectable } from '@angular/core';
import FileSaver from 'file-saver';
import { BehaviorSubject, Observable, map } from 'rxjs';
import {
  CombinedState,
  DryerDTO,
  DryerGroup,
  FormValues,
  ModelDTO,
  PayloadDto,
  PayloadSensorValues,
  ProductDTO,
  SelectedRow,
  SelectedValue,
  TransformedResponse,
} from '../models';
import { Images } from '../models/file.model';
import { Product } from '../models/product.model';
import { ProductRepository } from '../repositories/products.repository';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private productSource = new BehaviorSubject<string>('DRYERS');
  currentProduct$ = this.productSource.asObservable();

  private selectedDryerGroup = new BehaviorSubject<string>('');

  sidenavState: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private formStateSubject = new BehaviorSubject<CombinedState | null>(null);
  formState$Model = this.formStateSubject.asObservable();

  private formStateConditionSubject = new BehaviorSubject<CombinedState | null>(null);
  formState$Condition = this.formStateConditionSubject.asObservable();

  private fetchedDataSubject = new BehaviorSubject<ModelDTO[] | null>(null);
  fetchedData$ = this.fetchedDataSubject.asObservable();

  private modelDataSubject = new BehaviorSubject<ModelDTO[] | null>(null);
  modelData$ = this.modelDataSubject.asObservable();

  private sensorValuesSubject = new BehaviorSubject<PayloadSensorValues | null>(null);
  sensorValues$ = this.sensorValuesSubject.asObservable();

  private selectedValuesSubject = new BehaviorSubject<SelectedValue[]>([]);
  selectedValues$ = this.selectedValuesSubject.asObservable();

  private updatedGrandParentSubject = new BehaviorSubject<string[]>([]);
  grandParentSelectedValue$ = this.updatedGrandParentSubject.asObservable();

  private filteredSelectedRowsSubject = new BehaviorSubject<SelectedRow[]>([]);
  filteredSelectedRows$ = this.filteredSelectedRowsSubject.asObservable();

  state: any;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  private checkboxesLoadedSubject = new BehaviorSubject<boolean>(false);
  checkboxesLoaded$ = this.checkboxesLoadedSubject.asObservable();

  private formFieldValuesSubject = new BehaviorSubject<{ [key: string]: FormValues }>({});
  formFieldValues$ = this.formFieldValuesSubject.asObservable();

  private selectedCheckboxesSubject = new BehaviorSubject<SelectedRow[]>([]);
  selectedCheckboxes$ = this.selectedCheckboxesSubject.asObservable();

  private processedConditionResultsSubject = new BehaviorSubject<any[]>([]);

  private activeTabBehaviorSubject = new BehaviorSubject<string>('');
  activeTab$ = this.activeTabBehaviorSubject.asObservable();

  // Add separate storage for model form values
  private formFieldValuesModelSubject = new BehaviorSubject<{ [key: string]: FormValues }>({});
  formFieldValuesModel$ = this.formFieldValuesModelSubject.asObservable();

  constructor(private productRepository: ProductRepository) {}

  setFilteredSelectedRows(rows: SelectedRow[]): void {
    this.filteredSelectedRowsSubject.next(rows);
  }
  setLoading(loading: boolean) {
    this.loadingSubject.next(loading);
  }
  setCheckboxesLoaded(loaded: boolean) {
    this.checkboxesLoadedSubject.next(loaded);
  }
  setFormStateModel(state: CombinedState) {
    this.formStateSubject.next(state);
  }
  setSelectedDryerGroup(dryerGroup: string) {
    this.selectedDryerGroup.next(dryerGroup);
  }

  toggleSidenav() {
    this.sidenavState.next(!this.sidenavState.getValue());
  }

  getSelectedDryerGroup(): Observable<string> {
    return this.selectedDryerGroup.asObservable();
  }

  updateSensorValues(data: PayloadSensorValues | null) {
    this.sensorValuesSubject.next(data);
  }
  setFormStateCondition(state: CombinedState) {
    this.formStateConditionSubject.next(state);
  }

  getFormStateModel() {
    return this.formStateSubject.getValue();
  }

  getFormStateCondition() {
    return this.formStateConditionSubject.getValue();
  }

  updateSelectedValues(values: SelectedValue[]): void {
    this.selectedValuesSubject.next(values);
  }

  updateSelectedGrandParentValues(values: string[]): void {
    this.updatedGrandParentSubject.next(values);
  }
  updateFetchedData(data: ModelDTO[] | null) {
    this.fetchedDataSubject.next(data);
  }

  updateModelData(data: ModelDTO[] | null) {
    this.modelDataSubject.next(data);
  }
  changeProduct(product: string) {
    this.productSource.next(product);
  }

  getByProducts(): Observable<Product[]> {
    return this.productRepository.getProducts();
  }

  getImages(): Observable<Images[]> {
    return this.productRepository.getImages();
  }

  getActualImagePathUsingModelName(modelName: string): Observable<string> {
    return this.productRepository.fetchActualModelImagePath(modelName);
  }

  getImagePathUsingDryerGroup(dryerGroup: DryerGroup): Observable<Images> {
    return this.productRepository.getImagePathUsingDryerGroup(dryerGroup);
  }

  getDryerTypes(): Observable<TransformedResponse[]> {
    return this.productRepository.getDryerTypes().pipe(
      map((response) => {
        return this.transformResponse(response);
      }),
    );
  }

  private transformResponse(response: string): TransformedResponse[] {
    return Object.keys(response).map((grandparentKey) => ({
      checkboxLabel: grandparentKey,
      translationKey: this.mapToTranslationKey(grandparentKey),
      isChecked: true,
      isIndeterminate: false,
      isParent: true,
      children: Object.keys(response[grandparentKey]).map((parentKey) => ({
        checkboxLabel: parentKey,
        translationKey: this.mapToTranslationKey(parentKey),
        isChecked: true,
        isIndeterminate: false,
        isParent: true,
        children: response[grandparentKey][parentKey]
          .filter((child: string) => child !== null)
          .map((child: string) => ({
            checkboxLabel: child,
            translationKey: this.mapToTranslationKey(child),
            isChecked: true,
            isIndeterminate: false,
            isParent: false,
          })),
      })),
    }));
  }

  // Example: map backend string to your i18n keys
  private mapToTranslationKey(label: string): string {
    const translationMap: Record<string, string> = {
      'Subfreezing Dryers': 'SUBFREEZING_DRYERS',
      'Desiccant Dryers': 'DESICCANT_DRYERS',
      'Refrigerated Dryers': 'REFRIGERATED_DRYERS',
      'VSD Dryers': 'VSD_DRYERS',
      'Air Cooled': 'AIR_COOLED',
      'Water Cooled': 'WATER_COOLED',
      'Cycling Refrigerated': 'CYCLING_REFRIGERATED',
      'Non-Cycling Refrigerated': 'NON_CYCLING_REFRIGERATED',
      'High Temperature Refrigerated': 'HIGH_TEMPERATURE_REFRIGERATED',
      Heated: 'HEATED',
      Heatless: 'HEATLESS',
      'Desiccant Modular': 'DESICCANT_MODULAR',
      'Twin Tower': 'TWIN_TOWER',
      VSD: 'VSD',
    };

    return translationMap[label] || label;
  }

  getProductFields(payload: DryerDTO[]): Observable<Map<string, object>> {
    return this.productRepository.fetchFields(payload);
  }

  getAllModels(): Observable<string[]> {
    return this.productRepository.getAllModels();
  }

  getModelNames(params: DryerDTO[]): Observable<string[]> {
    return this.productRepository.fetchModelNames(params);
  }

  getExactModels(params: PayloadDto): Observable<ModelDTO[]> {
    return this.productRepository.fetchExactModels(params);
  }

  getSpecificModel(body: FormValues, specificModel: string): Observable<ModelDTO[]> {
    return this.productRepository.fetchSpecificModel(body, specificModel);
  }

  getSuggestedModels(exactModel: ProductDTO): Observable<Map<string, object>> {
    return this.productRepository.fetchSuggestedModels(exactModel);
  }

  // Update the setSelectedCheckbox method
  setSelectedCheckbox(data: SelectedRow[]) {
    this.state = data;
    this.selectedCheckboxesSubject.next(data);
  }
  getSelectedRows(): SelectedRow[] {
    return this.state || [];
  }
  clearState(): void {
    this.state = null;
  }

  getDownloadPdf(modelName: string, isHeaterRequired: boolean, fetchSensorValues: PayloadSensorValues) {
    this.productRepository.getDownloadPdf(modelName, isHeaterRequired, fetchSensorValues).subscribe((res) => {
      const blob = new Blob([res.body], { type: 'application/pdf' });
      FileSaver.saveAs(blob, `${modelName}.pdf`);
    });
  }
  // Add method to update form field values
  updateFormFieldValues(values: { [key: string]: FormValues }): void {
    const currentValues = this.formFieldValuesSubject.getValue();
    this.formFieldValuesSubject.next({ ...currentValues, ...values });
  }

  // Add method to get stored form field values
  getFormFieldValues(): { [key: string]: FormValues } {
    return this.formFieldValuesSubject.getValue();
  }

  // Add method to clear form field values
  clearFormFieldValues(): void {
    this.formFieldValuesSubject.next({});
  }

  updateProcessedConditionResults(rows: any[]): void {
    this.processedConditionResultsSubject.next(rows);
  }

  getProcessedConditionResults(): any[] {
    return this.processedConditionResultsSubject.getValue();
  }

  clearProcessedConditionResults(): void {
    this.processedConditionResultsSubject.next([]);
  }

  clearSelectedCheckboxes(): void {
    this.selectedCheckboxesSubject.next([]);
    this.setFilteredSelectedRows([]);
    this.state = null;
  }

  setActiveTab(tab: string): void {
    this.activeTabBehaviorSubject.next(tab);
  }

  // Add method to update model form field values
  updateFormFieldValuesModel(values: { [key: string]: FormValues }): void {
    const currentValues = this.formFieldValuesModelSubject.getValue();
    this.formFieldValuesModelSubject.next({ ...currentValues, ...values });
  }

  // Add method to get stored model form field values
  getFormFieldValuesModel(): { [key: string]: FormValues } {
    return this.formFieldValuesModelSubject.getValue();
  }

  // Add method to clear model form field values
  clearFormFieldValuesModel(): void {
    this.formFieldValuesModelSubject.next({});
  }

  // Reset all user-session product state. ProductsService is a root singleton, so on logout
  // (which router-navigates without a full reload) its in-memory state would otherwise persist
  // and leak into the next session — e.g. the condition/model forms restoring the previous
  // user's inputs. Called from LoginService.logout().
  clearAllState(): void {
    this.formFieldValuesSubject.next({});
    this.formFieldValuesModelSubject.next({});
    this.formStateSubject.next(null);
    this.formStateConditionSubject.next(null);
    this.processedConditionResultsSubject.next([]);
    this.fetchedDataSubject.next(null);
    this.modelDataSubject.next(null);
    this.sensorValuesSubject.next(null);
    this.selectedValuesSubject.next([]);
    this.updatedGrandParentSubject.next([]);
    this.selectedCheckboxesSubject.next([]);
    this.filteredSelectedRowsSubject.next([]);
    this.state = null;
  }

  // Add method to initialize model form values from condition form values
  initializeModelFormValues(): void {
    const conditionValues = this.formFieldValuesSubject.getValue();
    if (Object.keys(conditionValues).length > 0 && Object.keys(this.formFieldValuesModelSubject.getValue()).length === 0) {
      this.formFieldValuesModelSubject.next({ ...conditionValues });
    }
  }

  // Add method to initialize condition form values from model form values
  initializeConditionFormValues(): void {
    const modelValues = this.formFieldValuesModelSubject.getValue();
    const conditionValues = this.formFieldValuesSubject.getValue();

    if (Object.keys(modelValues).length > 0 && Object.keys(conditionValues).length === 0) {
      this.formFieldValuesSubject.next({ ...modelValues });
    }
  }
}
